import { LANDSCAPE_MEDIA_ASPECT_RATIO } from "../../EditLayout/mediaSpecs";
import { css } from "@emotion/react";
import { styleUtils, theme } from "@minted/minted-components";
import React, { memo } from "react";

/**
 * Side-by-side media treatment — two photos in a static horizontal row.
 * Each image shares the same aspect ratio it was cropped to at upload time
 * (rather than a fixed pixel height), so the row wraps to the actual photo
 * proportions and stays consistent at every viewport width. Design theme
 * does not style these images; layout CSS only.
 *
 * data-cy leftImage / rightImage match EditableHeader's modular pencil hooks.
 */
const styles = {
  image: css`
    aspect-ratio: ${LANDSCAPE_MEDIA_ASPECT_RATIO};
    display: block;
    flex: 1;
    object-fit: cover;
    width: 100%;
  `,
  row: css`
    align-items: stretch;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.x2};
    width: 100%;
  `,
  viewport: css`
    margin-bottom: ${styleUtils.rem(30)};
    width: 100%;
  `,
};

export type SideBySideMediaProps = {
  alt: string;
  leftPhotoURL: string;
  rightPhotoURL: string;
};

const SideBySideMedia = ({
  alt,
  leftPhotoURL,
  rightPhotoURL,
}: SideBySideMediaProps) => (
  <div css={styles.viewport} data-cy="sideBySideMedia">
    <div css={styles.row}>
      <img
        alt={alt}
        css={styles.image}
        data-cy="leftImage"
        src={leftPhotoURL}
      />
      <img
        alt={alt}
        css={styles.image}
        data-cy="rightImage"
        src={rightPhotoURL}
      />
    </div>
  </div>
);

const MemoizedSideBySideMedia = memo(SideBySideMedia);

export { MemoizedSideBySideMedia as SideBySideMedia };
